<script setup lang="ts">
import { useRouter } from 'vue-router'
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { useCurrentOrganizationStore } from '@/stores/currentOrganization'
import { storeToRefs } from 'pinia'
import Logo from './Logo.new.vue'

const router = useRouter()
const authStore = useAuthStore()
const profileStore = useProfileStore()
const currentOrganizationStore = useCurrentOrganizationStore()
const { userData, isAdmin } = storeToRefs(authStore)
const userProfile = computed(() => profileStore.profiles.find(p => p.id === userData.value?.profileId))
const isOpen = ref(false)
const isCollapsed = ref(false)
const isMobile = ref(false)

// Detecta se é dispositivo móvel
const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024
  if (isMobile.value) {
    isCollapsed.value = false
  }
}

// Fecha o menu ao clicar em links em dispositivos móveis
const handleNavigation = () => {
  if (isMobile.value) {
    isOpen.value = false
  }
}

// Fecha o menu ao pressionar ESC
const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false
  }
}

// Lifecycle hooks
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  document.addEventListener('keydown', handleEscKey)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
  document.removeEventListener('keydown', handleEscKey)
})

const menuItems = computed(() => {
  const items = [
    { 
      path: '/dashboard', 
      name: 'Dashboard', 
      icon: 'pi pi-home', 
      description: 'Painel principal com visão geral do sistema',
      requiresOrganization: true
    },
    { 
      path: '/accounts', 
      name: 'Contas a Pagar e Receber', 
      icon: 'pi pi-money-bill', 
      description: 'Gerenciamento de contas e transações',
      requiresOrganization: true
    },
    { 
      path: '/cash-flow', 
      name: 'Fluxo de Caixa', 
      icon: 'pi pi-chart-line', 
      description: 'Relatórios e análises financeiras',
      requiresOrganization: true
    },
    {
      path: 'https://bhissdigital.pbh.gov.br/nfse/',
      name: 'Prefeitura de BH',
      icon: 'pi pi-external-link',
      description: 'Portal de Notas Fiscais da Prefeitura de BH',
      requiresOrganization: true,
      external: true
    },
    {
      path: 'https://contadigital.inter.co/',
      name: 'Banco Inter',
      icon: 'pi pi-credit-card',
      description: 'Internet Banking do Banco Inter',
      requiresOrganization: true,
      external: true
    }
  ]

  // Filtrar os itens baseado nas permissões do perfil e se precisa de organização
  return items.filter(item => {
    if (!item.requiresOrganization || currentOrganizationStore.currentOrganization) {
      if (isAdmin.value) return true
      // Para links externos, usar o nome como chave de permissão
      const permissionKey = item.external ? item.name : item.path
      return userProfile.value?.menuPermissions?.[permissionKey] || false
    }
    return false
  })
})

const organizationSettings = computed(() => {
  const items = [
    { 
      path: '/banks', 
      name: 'Bancos', 
      icon: 'pi pi-building', 
      description: 'Configurações de bancos'
    },
    { 
      path: '/suppliers', 
      name: 'Fornecedores', 
      icon: 'pi pi-briefcase', 
      description: 'Gerenciamento de fornecedores'
    },
    { 
      path: '/customers', 
      name: 'Clientes', 
      icon: 'pi pi-users', 
      description: 'Gerenciamento de clientes'
    },
    { 
      path: '/cost-centers', 
      name: 'Centro de Custos', 
      icon: 'pi pi-chart-pie', 
      description: 'Gerenciamento de centro de custos'
    },
    { 
      path: '/payment-status', 
      name: 'Status de Pagamento', 
      icon: 'pi pi-check-circle', 
      description: 'Configurações de status de pagamento'
    }
  ]

  // Só mostra se tiver uma organização selecionada
  if (!currentOrganizationStore.currentOrganization) {
    return []
  }

  // Filtrar os itens baseado nas permissões do perfil
  return items.filter(item => {
    if (isAdmin.value) return true
    return userProfile.value?.menuPermissions?.[item.path] || false
  })
})

const generalSettings = computed(() => {
  const items = [
    { 
      path: '/user-management', 
      name: 'Gerenciar Usuários', 
      icon: 'pi pi-users', 
      description: 'Gerenciamento de usuários e permissões'
    },
    { 
      path: '/profile-management', 
      name: 'Perfis', 
      icon: 'pi pi-id-card', 
      description: 'Configurações de perfis e permissões'
    },
    { 
      path: '/my-account', 
      name: 'Minha Conta', 
      icon: 'pi pi-user', 
      description: 'Configurações da conta do usuário'
    }
  ]

  // Adicionar Organizações apenas para admin
  if (isAdmin.value) {
    items.push({ 
      path: '/organizations', 
      name: 'Organizações', 
      icon: 'pi pi-sitemap', 
      description: 'Gerenciamento de organizações'
    })
  }

  // Filtrar os itens baseado nas permissões do perfil
  return items.filter(item => {
    if (isAdmin.value) return true
    return userProfile.value?.menuPermissions?.[item.path] || false
  })
})

const showOrganizationSettings = computed(() => {
  // Só mostra se tiver uma organização selecionada e itens para mostrar
  return currentOrganizationStore.currentOrganization && organizationSettings.value.length > 0
})

const showGeneralSettings = computed(() => {
  // Sempre mostra se tiver itens, independente da organização
  return generalSettings.value.length > 0
})

const getItemPath = (item: { path: string }) => {
  // Remove a barra inicial para pegar o nome da rota
  const routeName = item.path.startsWith('/') ? item.path.slice(1) : item.path
  return { name: routeName }
}

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Função para obter as iniciais do nome
const getInitials = (name?: string) => {
  if (!name) return ''
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Função para obter o nome do perfil
const getUserProfile = (profileId?: string) => {
  if (!profileId) return ''
  const profile = profileStore.profiles.find(p => p.id === profileId)
  return profile?.name || ''
}
</script>

<template>
  <div class="relative">
    <!-- Mobile Menu Button -->
    <button 
      @click="isOpen = !isOpen"
      class="lg:hidden fixed top-0 left-0 z-20 h-16 w-16 flex items-center justify-center bg-transparent hover:bg-gray-50/50 transition-all"
      :class="{ 'left-[85vw]': isOpen && isMobile }"
      aria-label="Menu"
      v-tooltip="isOpen ? 'Fechar menu' : 'Abrir menu'"
    >
      <i :class="[isOpen ? 'pi pi-times' : 'pi pi-bars', 'text-xl text-gray-600']"></i>
    </button>

    <!-- Sidebar -->
    <aside 
      :class="[
        'bg-white border-r border-gray-100/75 flex flex-col transition-all duration-300 h-screen overflow-hidden',
        'fixed lg:static inset-y-0 left-0 z-40',
        isCollapsed ? 'w-16' : 'w-[85vw] sm:w-72 lg:w-72',
        isOpen ? 'translate-x-0 shadow-xl lg:shadow-none' : '-translate-x-full lg:translate-x-0',
        { 'touch-none': isOpen && isMobile }
      ]"
      :data-role="userData?.role"
    >
      <!-- Logo e Botão de Colapso -->
      <div class="border-b border-gray-100/75 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
        <div :class="['flex items-center transition-all duration-300', isCollapsed ? 'h-36 flex-col pt-4' : 'h-20 px-4']">
          <button
            v-if="!isMobile && isCollapsed"
            @click="isCollapsed = !isCollapsed"
            class="flex items-center justify-center w-8 h-8 bg-white rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 transition-all border border-gray-100/75 flex-shrink-0 mb-6"
            :aria-label="'Expandir menu'"
            v-tooltip="'Expandir menu'"
          >
            <i class="pi pi-angle-right text-gray-500 text-sm"></i>
          </button>
          
          <div :class="['flex-1 min-w-0 flex items-center relative', isCollapsed ? 'mb-8' : '']">
            <Logo :collapsed="isCollapsed" />
            <button
              v-if="!isMobile && !isCollapsed"
              @click="isCollapsed = !isCollapsed"
              class="absolute right-0 flex items-center justify-center w-8 h-8 bg-white rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 transition-all border border-gray-100/75 flex-shrink-0"
              :aria-label="'Recolher menu'"
              v-tooltip="'Recolher menu'"
            >
              <i class="pi pi-angle-left text-gray-500 text-sm"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto py-4 overscroll-contain">
        <!-- Menu Principal -->
        <ul class="space-y-1 px-3">
          <li v-for="item in menuItems" :key="item.path">
            <a
              v-if="item.external"
              :href="item.path"
              target="_blank"
              rel="noopener noreferrer"
              :class="[
                'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-all duration-75',
                isCollapsed ? 'justify-center' : '',
                $route.path === item.path
                  ? 'bg-primary-dark text-white hover:bg-primary-dark/90'
                  : 'text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5'
              ]"
              v-tooltip.right="isCollapsed ? item.name : ''"
              @click="handleNavigation"
            >
              <i :class="[item.icon, 'text-lg']"></i>
              <span :class="['whitespace-nowrap', isCollapsed ? 'hidden' : '']">{{ item.name }}</span>
            </a>
            <router-link
              v-else
              :to="getItemPath(item)"
              :class="[
                'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-all duration-75',
                isCollapsed ? 'justify-center' : '',
                $route.path === item.path
                  ? 'bg-primary-dark text-white hover:bg-primary-dark/90'
                  : 'text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5'
              ]"
              v-tooltip.right="isCollapsed ? item.name : ''"
              @click="handleNavigation"
            >
              <i :class="[item.icon, 'text-lg']"></i>
              <span :class="['whitespace-nowrap', isCollapsed ? 'hidden' : '']">{{ item.name }}</span>
            </router-link>
          </li>
        </ul>

        <!-- Configurações de Organização -->
        <div v-if="showOrganizationSettings" class="mt-8">
          <h2 v-if="!isCollapsed" class="px-6 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Configurações de Organização
          </h2>
          <ul class="space-y-1 px-3">
            <li v-for="item in organizationSettings" :key="item.path">
              <router-link
                :to="getItemPath(item)"
                :class="[
                  'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-all duration-75',
                  isCollapsed ? 'justify-center' : '',
                  $route.path === item.path
                    ? 'bg-primary-dark text-white hover:bg-primary-dark/90'
                    : 'text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5'
                ]"
                v-tooltip.right="isCollapsed ? item.name : ''"
                @click="handleNavigation"
              >
                <i :class="[item.icon, 'text-lg']"></i>
                <span :class="['whitespace-nowrap', isCollapsed ? 'hidden' : '']">{{ item.name }}</span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Configurações Gerais -->
        <div v-if="showGeneralSettings" class="mt-8">
          <h2 v-if="!isCollapsed" class="px-6 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Configurações Gerais
          </h2>
          <ul class="space-y-1 px-3">
            <li v-for="item in generalSettings" :key="item.path">
              <router-link
                :to="getItemPath(item)"
                :class="[
                  'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-all duration-75',
                  isCollapsed ? 'justify-center' : '',
                  $route.path === item.path
                    ? 'bg-primary-dark text-white hover:bg-primary-dark/90'
                    : 'text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5'
                ]"
                v-tooltip.right="isCollapsed ? item.name : ''"
                @click="handleNavigation"
              >
                <i :class="[item.icon, 'text-lg']"></i>
                <span :class="['whitespace-nowrap', isCollapsed ? 'hidden' : '']">{{ item.name }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Footer - User Profile -->
      <div class="border-t border-gray-100/75 mt-auto sticky bottom-0 bg-white/95 backdrop-blur-sm">
        <div class="p-3">
          <div 
            :class="[
              'flex items-center rounded-lg mb-2 transition-all',
              isCollapsed ? 'justify-center' : 'px-3 py-2'
            ]"
          >
            <div class="relative">
              <div 
                class="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium shadow-sm"
                v-if="!userData?.photoURL"
              >
                {{ getInitials(userData?.name) }}
              </div>
              <img
                v-else
                :src="userData?.photoURL"
                :alt="userData?.name"
                class="h-9 w-9 rounded-full object-cover shadow-sm"
              />
              <span 
                class="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white bg-green-400"
                aria-hidden="true"
              />
            </div>
            <div v-if="!isCollapsed" class="ml-3 min-w-0 flex-1">
              <div class="flex items-center justify-between">
                <p class="font-medium text-gray-900 text-sm truncate">{{ userData?.name }}</p>
                <router-link
                  to="/my-account"
                  class="ml-2 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors no-underline"
                  v-tooltip="'Minha Conta'"
                  @click="handleNavigation"
                >
                  <i class="pi pi-cog text-lg"></i>
                </router-link>
              </div>
              <span 
                class="inline-block px-2 py-0.5 text-xs rounded-full mt-0.5 font-medium"
                :class="[
                  {
                    'bg-primary-dark/10 text-primary-dark': userData?.role === 'admin',
                    'bg-primary-light/10 text-primary-light': userData?.role === 'manager' || userData?.role === 'user'
                  }
                ]"
              >
                {{ getUserProfile(userData?.profileId) }}
              </span>
            </div>
          </div>

          <!-- Logout Button -->
          <button
            @click="handleLogout"
            class="w-full flex items-center rounded-lg text-gray-500 transition-all active:bg-gray-100/75 hover:text-red-600 no-underline"
            :class="[
              isCollapsed ? 'justify-center w-10 h-10 mx-auto' : 'px-3 py-2.5 hover:bg-red-50/75'
            ]"
          >
            <i class="pi pi-sign-out text-xl" :class="[isCollapsed ? '' : 'mr-3 w-5']"></i>
            <span v-if="!isCollapsed" class="whitespace-nowrap">Sair</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Overlay -->
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-gray-600/20 transition-opacity lg:hidden backdrop-blur-sm"
      style="z-index: 35;"
      @click="isOpen = false"
    ></div>
  </div>
</template>

<style scoped>
.router-link-active {
  font-weight: 500;
  position: relative;
}

/* Remove sublinhado de todos os links */
a {
  text-decoration: none !important;
}

.router-link-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  @apply bg-primary-dark rounded-r;
}

[data-role="manager"] .router-link-active::before,
[data-role="user"] .router-link-active::before {
  @apply bg-primary-light;
}

.menu-item-collapsed {
  @apply hover:bg-gray-100;
}

.menu-item-collapsed.router-link-active::before {
  left: -0.5rem;
}

/* Estilização da barra de rolagem */
nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 2px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: #D1D5DB;
}

/* Previne scroll do body quando menu mobile está aberto */
:root {
  overflow: auto;
  height: 100%;
}

:root.modal-open {
  overflow: hidden;
}
</style>